import { t } from "@lingui/macro";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { ScrollView, TextInput } from "react-native";

import type { useSignInMutation$data } from "@/features/Authentication/hooks/__generated__/useSignInMutation.graphql";
import { Button } from "@whoosh/design-system/src/components/controls";
import { SubtleInput } from "@whoosh/design-system/src/components/controls/SubtleInput";
import {
  EyeClosedIcon,
  EyeIcon,
  InfoIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Text,
  Pressable,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { usePasswordReset } from "../../../../../../../../../screens/ResetPassword/hooks/usePasswordReset";
import { useSignIn } from "../../../../../../../hooks/useSignIn";
import { proceedToJoinNewClubStepAtom } from "../../../../atoms/actions";
import { VerificationModalStepStateAtom } from "../../../../atoms/stores";

const currentStepDataAom = atom((get) => {
  const curStateData = get(VerificationModalStepStateAtom);
  if (curStateData.step === "ResetPassword") {
    return curStateData;
  }
  return null;
});

export const ResetPasswordStep = ({
  clubSlug,
  onLoginCompleted,
}: {
  clubSlug?: string;
  onLoginCompleted: (data: useSignInMutation$data) => void;
}) => {
  const [inputIsPassword, setInputIsPassword] = useState(true);
  const [curPassword, setCurPassword] = useState("");
  const inputRef = useRef<TextInput>(null);
  const [isFocused, setIsFocused] = useState(false);

  const curStepData = useAtomValue(currentStepDataAom);

  const canSubmit = !!curStepData?.verifiedEmail && curPassword.length >= 8;
  const [completeLogin] = useSignIn();

  const proceedToJoinClub = useSetAtom(proceedToJoinNewClubStepAtom);

  const [resetPassword, isResettingPassword] = usePasswordReset({
    onComplete: () => {
      completeLogin(
        { email: curStepData?.verifiedEmail ?? "", password: curPassword },
        (res) => {
          const clientType =
            res.responsePayload?.session?.currentClientProfile?.member
              ?.__typename;

          if (clientType === "AnonymousCustomer") {
            proceedToJoinClub();
          } else {
            onLoginCompleted(res);
          }
        }
      );
    },
  });

  const onUpdatePassword = () => {
    if (curStepData?.token && curPassword && curStepData?.verifiedEmail)
      resetPassword({
        password: curPassword,
        passwordConfirmation: curPassword,
        token: curStepData.token,
        userContext: clubSlug,
      });
  };

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [inputIsPassword]);

  const inputHasError = !canSubmit && !isFocused;

  return (
    <Box flex={1} paddingHorizontal={6}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <VStack paddingBottom={6} flexGrow={1}>
          <Text
            marginBottom={4}
            variant="mainSmallMedium"
            color="text-secondary"
          >
            {curStepData?.verifiedEmail}
          </Text>
          <VStack mb={6}>
            <Text variant={"mainTitle2"}>{t`Reset Password`}</Text>
          </VStack>
          <SubtleInput
            inputRef={inputRef}
            placeholder={t`Your Password`}
            name="password"
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            type={inputIsPassword ? "password" : "text"}
            value={curPassword}
            onChange={setCurPassword}
            hasError={inputHasError}
            rightIcon={
              <Pressable
                onPress={() => {
                  setInputIsPassword((val) => !val);
                }}
              >
                {inputIsPassword ? <EyeIcon /> : <EyeClosedIcon />}
              </Pressable>
            }
          />
          <Box marginTop={4}>
            <Box flexDirection="row" gap={1}>
              <InfoIcon color="text-secondary" />
              <Text variant="mainTiny" color="text-secondary">
                {t`At least 8 characters`}
              </Text>
            </Box>
          </Box>
        </VStack>
      </ScrollView>
      <VStack>
        <Button
          isDisabled={!canSubmit}
          onPress={onUpdatePassword}
          isLoading={isResettingPassword}
          accessibilityLabel={t`Reset Password button`}
        >{t`Reset Password`}</Button>
      </VStack>
    </Box>
  );
};
